import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kyPublicToken = searchParams.get('ky') || '';
  const { createAccount, privateUrl } = useAuth();
  usePageMeta('Create Account — Seriously', 'Join Seriously, the voice-first dating app. Create your free account — no paywalls, no tricks, just real connections.');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentBiometric, setConsentBiometric] = useState(false);
  const [consentPreferences, setConsentPreferences] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!dateOfBirth) {
      setError('Please enter your date of birth');
      return;
    }

    // Client-side age check
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 18) {
      setError('You must be at least 18 years old to use this service');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!consentTerms) {
      setError('You must accept the Terms of Service');
      return;
    }

    if (!consentPrivacy) {
      setError('You must accept the Privacy Policy');
      return;
    }

    if (!consentBiometric) {
      setError('You must consent to biometric data processing');
      return;
    }

    if (!consentPreferences) {
      setError('You must consent to dating preference processing');
      return;
    }

    setLoading(true);
    try {
      await createAccount(password, dateOfBirth, phoneNumber, consentTerms, consentPrivacy, consentBiometric, consentPreferences, kyPublicToken);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (privateUrl) {
      const fullUrl = `${window.location.origin}${privateUrl}`;
      navigator.clipboard.writeText(fullUrl);
      setShowCopied(true);
      setSavedConfirmed(true);
    }
  };

  if (privateUrl) {
    const fullUrl = `${window.location.origin}${privateUrl}`;

    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center" color="success.main" fontWeight="bold">
              Account Created!
            </Typography>

            <Alert severity="warning" sx={{ my: 3 }}>
              <Typography fontWeight="bold" gutterBottom>
                SAVE THIS URL!
              </Typography>
              <Typography variant="body2">
                This is your private URL. You need it to log in. There is no password recovery!
              </Typography>
            </Alert>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "secondary.main",
                mb: 2,
                position: 'relative',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  color: "primary.main",
                }}
              >
                {fullUrl}
              </Typography>
              <IconButton
                onClick={copyToClipboard}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Paper>

            <FormControlLabel
              control={
                <Checkbox
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" fontWeight="bold">
                  I have saved my private URL
                </Typography>
              }
              sx={{ mt: 2 }}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => navigate('/home')}
              disabled={!savedConfirmed}
              sx={{ mt: 1 }}
            >
              I've saved it — continue
            </Button>
          </Paper>

          <Snackbar
            open={showCopied}
            autoHideDuration={2000}
            onClose={() => setShowCopied(false)}
            message="URL copied to clipboard!"
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
            Create Account
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Create a password to secure your account. You'll receive a private URL to access your account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="date"
              label="Date of Birth"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split('T')[0],
              }}
              helperText="You must be at least 18 years old"
            />

            <TextField
              fullWidth
              type="tel"
              label="Phone Number"
              placeholder="+44 7123 456789"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              margin="normal"
              helperText="We'll text you when Seriously goes live in your area. International format (e.g. +44...)"
            />

            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText="Minimum 8 characters"
            />

            <TextField
              fullWidth
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentTerms}
                    onChange={(e) => setConsentTerms(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link component={RouterLink} to="/terms" target="_blank">
                      Terms of Service
                    </Link>
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPrivacy}
                    onChange={(e) => setConsentPrivacy(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link component={RouterLink} to="/privacy" target="_blank">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentBiometric}
                    onChange={(e) => setConsentBiometric(e.target.checked)}
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      I consent to my photo and voice being processed for matching purposes
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Your photo and voice help us verify you're real and find compatible matches.
                      They are only visible to potential matches on the platform, never sold to third parties, and you can delete them anytime from your profile.
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPreferences}
                    onChange={(e) => setConsentPreferences(e.target.checked)}
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      I consent to my dating preferences being processed for matching
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Your interactions (likes, skips, conversations) reveal relationship and attraction preferences.
                      We process this solely to find compatible matches — never shared with third parties or used for profiling beyond this app.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Back to Home
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateAccount;
